import { useEffect, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import useGetBookingWonPrize from "commons/hooks/useGetBookingWonPrize";

// Debounce Hook
const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const useGetRealTimeBookingWonPrize = ({
  projectId,
  searchTerm,
  formattedDateRange,
  currentPage,
  pageSize,
}) => {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [networkConnected, setNetworkConnected] = useState(true);
  const connectionRef = useRef(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedDateRange = useDebounce(formattedDateRange, 500);
  const debouncedCurrentPage = useDebounce(currentPage, 0);

  const {
    data: bookingWonPrizeData,
    totalItemCount,
    refetch,
    loading: bookingWonPrizeLoading,
  } = useGetBookingWonPrize(
    projectId,
    debouncedSearchTerm,
    debouncedDateRange,
    null,
    pageSize,
    debouncedCurrentPage - 1
  );

  const disconnect = useCallback(async () => {
    const connection = connectionRef.current;
    if (connection) {
      try {
        if (connection.state === signalR.HubConnectionState.Connected) {
          await connection.invoke("LeaveBookingWonPrizeRoom", {
            projectIdFilter: projectId,
          });
        }
        await connection.stop(); // 👉 thủ công disconnect
        console.log("🔌 SignalR disconnected manually.");
      } catch (err) {
        console.warn("Error stopping SignalR:", err);
      } finally {
        setConnected(false);
      }
    }
  }, [projectId]);

  const joinRoom = useCallback(
    async (connection) => {
      try {
        await connection.invoke("JoinBookingWonPrizeRoom", {
          projectIdFilter: projectId,
        });
      } catch (err) {
        console.warn("Join room error:", err);
      }
    },
    [projectId]
  );

  const connectToSignalR = useCallback(async () => {
    if (!projectId) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${process.env.REACT_APP_API}signalr-booking-won-prize?ProjectIdFilter=${projectId}`
      )
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    connectionRef.current = connection;

    connection.on("JoinBookingWonPrizeRoomConfirmation", (response) => {
      if (!response.connected) {
        console.warn("Join room failed:", response);
        refetch();
        setConnected(false);
        setLoading(false);
        return;
      }

      refetch();
      setConnected(true);
      setLoading(false);
    });

    connection.on("LeaveBookingWonPrizeRoomConfirmation", (msg) => {
      console.log("Left room:", msg);
    });

    connection.on("ReceiveBookingUpdate", () => {
      refetch();
    });

    connection.onreconnecting(() => {
      console.warn("🔄 SignalR reconnecting...");
      setConnected(false);
    });

    connection.onreconnected(() => {
      console.log("✅ SignalR reconnected");
      setConnected(true);
      joinRoom(connectionRef.current); // ⚠️ cần join lại room
      refetch();
    });

    connection.onclose((error) => {
      console.warn("❌ SignalR connection closed.", error);
      setConnected(false);
    });

    try {
      await connection.start();
      console.log("✅ SignalR connected");
      await joinRoom(connection);
      setConnected(true);
      setNetworkConnected(true);
    } catch (err) {
      console.error("🚨 SignalR connection error:", err);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refetch]);

  // 👉 Kết nối lại khi projectId thay đổi
  useEffect(() => {
    if (!projectId) return;

    const setupConnection = async () => {
      await disconnect();
      await connectToSignalR();
    };

    setupConnection();

    return () => {
      disconnect();
    };
  }, [projectId, connectToSignalR, disconnect]);

  // 👉 Theo dõi mất mạng
  useEffect(() => {
    const handleOffline = () => {
      const connection = connectionRef.current;
      console.warn("🔌 Offline – SignalR connection will break");
      connection.stop();
      setConnected(false);
      setNetworkConnected(false);
    };

    const handleOnline = async () => {
      console.log("🌐 Back online – try to restart connection if needed");
      const connection = connectionRef.current;
      if (connection) {
        try {
          await connection.start();
          await joinRoom(connection);
          setConnected(true);
          setNetworkConnected(true);
          refetch();
          console.log("✅ Reconnected after coming back online");
        } catch (err) {
          console.warn("⚠️ Reconnect failed on online event:", err);
        }
      }
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [refetch, joinRoom]);

  return {
    data: bookingWonPrizeData,
    loading: loading || bookingWonPrizeLoading,
    refetch,
    totalItemCount,
    connected,
    networkConnected,
    disconnect,
  };
};

export default useGetRealTimeBookingWonPrize;
